/* eslint-disable */
// ============================================================
// devCardModel — the pure read of ONE development for the v3 card and the
// based-on peek. Nothing here renders; every rule the card shows is decided
// here once, from the live helpers (devCardUtils · devLock · devOverdue ·
// SelectionChip · devNoKey), so the card and the peek can never disagree.
// ============================================================
import { isCompleted, isStopped, getDaysInStage, getShapes, getBoData, formatDate } from '../../../utils/devCardUtils';
import { devIsMovedToOrder, devLock, devLeftStageLabel } from '../../../utils/devLock';
import { isDevOverdue, devDaysPastDue } from '../../../utils/devOverdue';
import { resolveSelectionDecision, procurementLabel } from '../../developments/SelectionChip';
import { devNoKey, devNoBase } from '../../../utils/devNoKey';

// The six stage keys of the spectrum, with the prototype's palette (identical to
// the --st-* tokens on .hubRoot; repeated here because a card lives OUTSIDE the
// chrome's DOM and inline-sets its own --stage / --stageInk / --stageSoft).
export const STAGE_META = {
  draw:  { k: 'draw',  name: 'Drawing',        c: '#caa04a', ink: '#8a6518', soft: '#fff3d6', step: 1 },
  hand:  { k: 'hand',  name: 'Hand made',      c: '#6481a6', ink: '#3a5575', soft: '#e9eef5', step: 2 },
  color: { k: 'color', name: 'Colour sample',  c: '#c8977a', ink: '#84502e', soft: '#faf0e4', step: 3 },
  done:  { k: 'done',  name: 'Completed',      c: '#5a9e7c', ink: '#2f6b4c', soft: '#eaf5ef', step: 4 },
  lock:  { k: 'lock',  name: 'Moved to order', c: '#dc9d74', ink: '#8a4f26', soft: '#fdf5ef', step: 4 },
  stop:  { k: 'stop',  name: 'Stopped',        c: '#9a8b96', ink: '#5c4a5a', soft: '#f3eff2', step: 0 },
};
const STAGE_BY_STATUS = { 'Drawing stage': 'draw', 'Hand made sample stage': 'hand', 'Color sample stage': 'color' };

export function stageOf(dev) {
  if (!dev) return STAGE_META.draw;
  if (devIsMovedToOrder(dev)) return STAGE_META.lock;
  if (isStopped(dev)) {
    const r = dev.stop_reason || dev.data?.stop_reason;
    return { ...STAGE_META.stop, name: r === 'paused' ? 'Paused' : r === 'cancelled' ? 'Cancelled' : 'Stopped' };
  }
  if (isCompleted(dev)) return STAGE_META.done;
  return STAGE_META[STAGE_BY_STATUS[(dev.stage_status || dev.data?.stage_status || '').trim()] || 'draw'];
}

const str = (v) => (v == null ? '' : String(v));
const imgUrl = (img) => (typeof img === 'string' ? img : (img && img.url) || null);

// The images the carousel walks — Convention #78 reorder (primary first), as the legacy card.
export function devImages(dev) {
  const raw = dev.images?.length ? dev.images : (dev.data?.images || []);
  const pidx = dev.data?.primaryImageIndex ?? dev.primaryImageIndex ?? 0;
  const list = pidx > 0 && pidx < raw.length ? [raw[pidx], ...raw.filter((_, i) => i !== pidx)] : raw;
  return list.map(imgUrl).filter(Boolean);
}

// Based-on — one of three kinds. `catalog` and `ref` come from based_on /
// based_on_data (the four live vocab values); `dev` is the variant link: a dev
// number carrying a letter whose base exists in the loaded list (the OG rule, #188).
const baseIndexCache = new WeakMap();
export function baseIndex(allDevs) {
  if (!Array.isArray(allDevs)) return null;
  let m = baseIndexCache.get(allDevs);
  if (m) return m;
  m = new Map();
  for (const d of allDevs) {
    const key = devNoKey(d && d.dev_number);
    if (!key) continue;
    const base = key.replace(/ OG$/, '');
    // an explicit OG wins the base slot; a plain base keeps it only while nothing claims it
    if (key.endsWith(' OG') || !m.has(base)) m.set(base, d);
  }
  baseIndexCache.set(allDevs, m);
  return m;
}

export function basedOnOf(dev, allDevs, catalogItems) {
  const bo = dev.based_on || dev.data?.based_on;
  const bod = dev.based_on_data || dev.data?.based_on_data;
  if (bo === 'Original Item (Catalog)') {
    const item = bod?.item_id ? (catalogItems || []).find((i) => i.id === bod.item_id) : null;
    const data = item?.data || {};
    return {
      kind: 'catalog', itemId: bod?.item_id || null,
      brand: data.brand || item?.brand || bod?.brand || 'Originals item',
      sku: data.sku || item?.sku || bod?.sku || '',
      image: imgUrl((item?.images || [])[0]) || imgUrl(bod?.ref_image),
      shape: data.shape1 || data.shape || '', gender: data.gender || '', material: data.material || '',
      sun: /sun/i.test(str(data.frameCategory || data.category || data.type)) ? true : /opt/i.test(str(data.frameCategory || data.category || data.type)) ? false : null,
      devsCount: Array.isArray(allDevs) && bod?.item_id ? allDevs.reduce((n, d) => n + ((d.based_on_data || d.data?.based_on_data)?.item_id === bod.item_id ? 1 : 0), 0) : null,
    };
  }
  if (bo) {
    const g = getBoData(dev, catalogItems) || {};
    return { kind: 'ref', label: bo === 'Store Model (CL)' ? 'store model' : bo === 'Other' ? 'reference' : (bod?.sub_type === 'fty' ? 'factory sample' : 'brand reference'), name: g.name || bo, ref: g.ref || '', image: imgUrl(g.image) };
  }
  const key = devNoKey(dev.dev_number);
  if (key && /[A-Z]( OG)?$/.test(key) && /\d[A-Z]( OG)?$/.test(key)) {
    const base = devNoBase(key);
    const idx = baseIndex(allDevs);
    const target = idx && idx.get(base);
    if (target && target.id !== dev.id) {
      const tKey = devNoKey(target.dev_number) || base;
      return { kind: 'dev', dev: target, no: base, og: tKey.endsWith(' OG'), stage: stageOf(target), designer: target.designer || target.data?.designer || '—', image: devImages(target)[0] || null, shape: getShapes(target)[0] || '' };
    }
  }
  return null;
}

export function devCardModel(dev, allDevs, catalogItems) {
  const stage = stageOf(dev);
  const locked = stage.k === 'lock';
  const done = stage.k === 'done';
  const stopped = stage.k === 'stop';
  const overdue = !locked && !done && !stopped && isDevOverdue(dev);
  const days = getDaysInStage(dev);
  const key = devNoKey(dev.dev_number) || '';
  const no = key.replace(/ OG$/, '') || str(dev.dev_number) || '—';
  const og = key.endsWith(' OG');
  const sel = resolveSelectionDecision(dev.selections || dev.data?.selections);
  const lock = locked ? devLock(dev) : null;
  const shapes = getShapes(dev);
  const fty = str(dev.fty || dev.data?.fty);
  const ftyCode = fty.split(/\s*[—-]\s*/)[0].trim();
  const ftyName = fty.includes('—') ? fty.split('—').slice(1).join('—').trim() : '';
  const startDate = formatDate(dev.stageStartedAt || dev.data?.stageStartedAt || dev.created_at || dev.data?.created_at) || '';
  const season = str(dev.season || dev.data?.season).replace(/^Sunglasses\s+/i, '').replace(/^Optical\s+/i, 'Optic ');
  let pill;
  if (done) pill = { k: 'done', text: 'Done', em: formatDate(dev.completedAt || dev.data?.completedAt || dev.updated_at) || '' };
  else if (stopped) pill = { k: 'stop', text: stage.name, em: `${days || '—'}d` };
  else if (locked) pill = { k: 'lock', text: 'Locked', em: lock?.leftStage ? `left ${devLeftStageLabel(dev).replace(' sample stage', '').replace(' stage', '').toLowerCase()}` : 'to order' };
  else if (overdue) pill = { k: 'late', text: 'Overdue', em: `${devDaysPastDue(dev)}d` };
  else pill = { k: 'prog', text: 'In progress', em: `${days || 1}d` };
  return {
    stage, locked, done, stopped, overdue, days, no, og, pill,
    sel: sel ? { decision: sel.decision, season: sel.season, proc: procurementLabel(sel.procurement) } : null,
    designer: str(dev.designer || dev.data?.designer) || '—',
    ftyCode: ftyCode || '—', ftyName,
    material: str(dev.material || dev.data?.material),
    shape: shapes[0] || '', shapes,
    season, startDate,
    images: devImages(dev),
    basedOn: basedOnOf(dev, allDevs, catalogItems),
  };
}
